import { NavLink, Outlet, useNavigate, Navigate, Link } from "react-router-dom";
import { useState } from "react";
import { Home, User, Wallet, TrendingUp, Users, Bell, HelpCircle, Landmark, FileCheck, ClipboardList, Scale, ArrowDownToLine, ShieldCheck,
  CheckSquare, BarChart3, Settings, ScrollText, Menu, LogOut, LayoutDashboard, HandCoins, UserCheck, Group, BookOpen, KeyRound, Inbox, Megaphone, FileDown, Contact as Users2 } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/Auth";
import { Loading } from "@/components/common";

export function DemoBanner() {
  return null;
}

export function Logo({ light }) {
  return (
    <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
      <img src="/logo.png" alt="Tiyeni Capital" className="h-11 w-11 rounded-xl bg-black object-cover object-top ring-1 ring-sky-900/20" style={{ objectPosition: "50% 18%" }} />
      <span className={`font-heading text-lg font-bold leading-none ${light ? "text-white" : "text-brand"}`}>Tiyeni Capital<span className={`block text-[10px] font-medium tracking-wide ${light ? "text-sky-200" : "text-[#0b63f6]"}`}>Save. Invest. Grow together.</span></span>
    </Link>
  );
}

function memberNav(user) {
  const r = user.roles || [];
  const items = [{ to: "/app", label: "Home", icon: Home, end: true }];
  if (user.status === "active") items.push({ to: "/app/profile", label: "My Profile", icon: User });
  if (r.includes("member")) items.push({ to: "/app/account", label: "My Account", icon: Wallet }, { to: "/app/investments", label: "My Investments", icon: TrendingUp });
  if (r.includes("borrower")) items.push({ to: "/app/loans", label: "My Loans", icon: HandCoins });
  if (user.status === "active") items.push({ to: "/app/groups", label: "Village Banking", icon: Users });
  items.push({ to: "/app/notifications", label: "Notifications", icon: Bell, badge: user.unread }, { to: "/app/help", label: "Help", icon: HelpCircle }, { to: "/app/glossary", label: "Glossary", icon: BookOpen }, { to: "/app/guides", label: "Guides", icon: FileDown }, { to: "/app/password", label: "Change password", icon: KeyRound });
  return items;
}

const STAFF = [
  { to: "/staff", label: "Overview", icon: LayoutDashboard, p: "view_reports", end: true },
  { to: "/staff/applications", label: "New sign-ups", icon: FileCheck, p: "review_applications" },
  { to: "/staff/customers", label: "Customers", icon: Users2, p: "verify_users" },
  { to: "/staff/users", label: "ID checks", icon: UserCheck, p: "verify_users" },
  { to: "/staff/borrowers", label: "Borrowers", icon: ClipboardList, p: "manage_borrowers" },
  { to: "/staff/loans", label: "Loans", icon: Landmark, p: "view_staff" },
  { to: "/staff/payments", label: "Confirm payments", icon: Inbox, p: "view_staff" },
  { to: "/staff/reconciliation", label: "Bank records check", icon: Scale, p: "reconcile" },
  { to: "/staff/withdrawals", label: "Payouts (24h)", icon: ArrowDownToLine, p: "process_withdrawals" },
  { to: "/staff/capital", label: "Company money", icon: ShieldCheck, p: "view_reports" },
  { to: "/staff/approvals", label: "Needs my OK", icon: CheckSquare, p: "approve_material" },
  { to: "/staff/groups", label: "Village groups", icon: Group, p: "manage_groups_review" },
  { to: "/staff/reports", label: "Downloads", icon: BarChart3, p: "view_staff" },
  { to: "/staff/settings", label: "Settings", icon: Settings, p: "view_staff" },
  { to: "/staff/audit", label: "Activity history", icon: ScrollText, p: "view_audit" },
  { to: "/staff/referrals", label: "Invite & earn", icon: Megaphone, p: "view_staff" },
  { to: "/staff/glossary", label: "Glossary", icon: BookOpen },
  { to: "/staff/guides", label: "Guides", icon: FileDown },
  { to: "/staff/password", label: "Change password", icon: KeyRound },
];

function NavList({ items, onNav }) {
  return (
    <nav className="space-y-1">
      {items.map((i) => (
        <NavLink key={i.to} to={i.to} end={i.end} onClick={onNav} data-testid={`nav-${i.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] font-medium transition-colors ${isActive ? "bg-[#0369a1] text-white" : "text-stone-700 hover:bg-sky-50"}`}>
          <i.icon className="h-[18px] w-[18px]" />
          <span className="flex-1">{i.label}</span>
          {!!i.badge && <span className="rounded-full bg-[#d9a022] px-2 text-xs font-bold text-white">{i.badge}</span>}
        </NavLink>
      ))}
    </nav>
  );
}

export default function Layout({ staff }) {
  const { user, signOut, can, isStaff } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (staff && !isStaff) return <Navigate to="/app" replace />;
  const items = staff ? STAFF.filter((i) => !i.p || can(i.p)) : memberNav(user);
  const side = (
    <div className="flex h-full flex-col gap-6 p-4">
      {isStaff && (
        <div className="grid grid-cols-2 gap-1 rounded-lg bg-stone-100 p-1 text-sm font-semibold">
          <button onClick={() => { nav("/app"); setOpen(false); }} data-testid="switch-member-view" className={`rounded-md py-1.5 ${!staff ? "bg-white shadow-sm text-brand" : "text-stone-500"}`}>My money</button>
          <button onClick={() => { nav(can("view_reports") ? "/staff" : "/staff/loans"); setOpen(false); }} data-testid="switch-staff-view" className={`rounded-md py-1.5 ${staff ? "bg-white shadow-sm text-brand" : "text-stone-500"}`}>Staff</button>
        </div>
      )}
      <NavList items={items} onNav={() => setOpen(false)} />
      <div className="mt-auto rounded-lg border border-stone-200 bg-stone-50 p-3 text-xs text-stone-600">
        <div className="font-semibold text-stone-800" data-testid="current-user-name">{user.full_name}</div>
        <div className="mt-0.5">{user.roles.join(" · ").replace(/_/g, " ")}</div>
        <Button variant="ghost" size="sm" className="mt-2 -ml-2 h-8 text-stone-700" onClick={() => { signOut(); nav("/login"); }} data-testid="logout-button"><LogOut className="mr-1.5 h-4 w-4" /> Sign out</Button>
      </div>
    </div>
  );
  return (
    <div className="min-h-screen">
      <DemoBanner />
      <header className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-sky-900/10 bg-white/90 px-4 backdrop-blur-md lg:hidden">
        <Logo />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild><Button variant="outline" size="icon" data-testid="mobile-menu-button" aria-label="Open menu"><Menu className="h-5 w-5" /></Button></SheetTrigger>
          <SheetContent side="left" className="w-72 overflow-y-auto p-0">{side}</SheetContent>
        </Sheet>
      </header>
      <div className="flex">
        <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col overflow-y-auto border-r border-sky-900/10 bg-white/80 backdrop-blur lg:flex">
          <div className="border-b border-stone-100 p-4"><Logo /></div>
          {side}
        </aside>
        <main className="min-w-0 flex-1 px-4 py-8 sm:px-6 lg:px-10 lg:py-10"><div className="mx-auto max-w-6xl"><Outlet /></div></main>
      </div>
    </div>
  );
}
